import React from 'react'
import { useTranslation } from 'react-i18next'
import { SkeletonForm } from '@node-space/storybook-components'
import { Box, Button, Icon } from '@node-space/ui-components'
import { Description } from 'components/Text'
import { useVerificationContext } from 'hooks/context/useVerificationContext'
import { useIdVerification } from 'hooks/useIdVerification'
import { FormBody } from 'pages/Verification/_components/FormBody'
import { FormGetError } from 'pages/Verification/_components/FormGetError'
import { FormSubmitButton } from 'pages/Verification/_components/FormSubmitButton'
import { NameTile, NameTilesContanier } from 'pages/Verification/_components/NameTile'
import { AccountContactActionError } from './AccountContactActionError'
import { AccountContactModal } from './AccountContactModal'
import { AccountContactErrorTypes } from './helpers/constants'
import { AccountContactData, AccountContactType } from './helpers/types'
import { getAccountContactName, getContactTypeTranslation } from './helpers/utils'
import { useAccountContactActions } from './hooks/useAccountContactActions'
import { useAccountContactList } from './hooks/useAccountContactList'

export interface AccountContactStepProps {
  accountContactType: AccountContactType
}

export const AccountContactStep = ({ accountContactType }: AccountContactStepProps) => {
  const { t } = useTranslation()
  const { isIdVerificationEnabled } = useIdVerification()
  const { accountContacts, submitStep } = useVerificationContext()
  const { isControllerRequired } = useAccountContactList(accountContacts?.list)

  const {
    contactList,
    actionsData,
    selectedAccountContact,
    deletingAccountContacts,
    isDetailsOpen,
    errors,
    getIsAccountContactsValid,
    onAddDetails,
    onUpdateDetails,
    onSave,
    onDelete,
    onCloseDetails,
  } = useAccountContactActions(accountContactType, accountContacts)

  const handleContinue = () => {
    const isValid = getIsAccountContactsValid()
    if (!isValid) return
    submitStep()
  }

  const contactTypeTranslation = t(getContactTypeTranslation(accountContactType))

  return accountContacts?.isFetching ? (
    <SkeletonForm name="Account contacts" rowCount={1} />
  ) : accountContacts?.isFetchingError ? (
    <FormGetError />
  ) : (
    <>
      <FormBody>
        {!!errors?.actionErrorStatus?.errorType && !isDetailsOpen && (
          <AccountContactActionError
            {...errors?.actionErrorStatus}
            accountContactType={accountContactType}
          />
        )}
        {!!contactList?.length && (
          <Box flexCol gap={8}>
            <NameTilesContanier>
              {contactList?.map(({ reference, ...contact }: AccountContactData) => {
                const contactFullName = getAccountContactName(contact)
                return (
                  <NameTile
                    key={reference}
                    {...(isControllerRequired &&
                      !!contact?.controllerInfo && { tag: t('controller.title') })}
                    tileId={reference}
                    label={contactFullName}
                    isLoading={deletingAccountContacts?.includes(reference)}
                    onEdit={() => onUpdateDetails(reference)}
                    onDelete={() => onDelete({ reference, contactFullName })}
                  />
                )
              })}
            </NameTilesContanier>
            {isIdVerificationEnabled && (
              <Description>
                {t('accountContact.createIdVerificationLink.linkProvidedLater')}
              </Description>
            )}
          </Box>
        )}
        <Box>
          <Button onClick={onAddDetails} variant="secondary" iconElement={<Icon name="Plus" />}>
            {t('accountContact.addAContact', { contactType: contactTypeTranslation })}
          </Button>
        </Box>
        <FormSubmitButton
          disabled={
            errors?.actionErrorStatus?.errorType === AccountContactErrorTypes.EMPTY_CONTACTS
          }
          onClick={handleContinue}
              testId='continue-button'
        />
      </FormBody>
      {isDetailsOpen && (
        <AccountContactModal
          contactType={contactTypeTranslation}
          initialData={selectedAccountContact}
          actionsData={actionsData}
          actionErrorStatus={errors?.actionErrorStatus}
          onSave={onSave}
          onDelete={onDelete}
          onClose={onCloseDetails}
        />
      )}
    </>
  )
}
